import type { MeteorsEffect } from '../../../data/full/celeste';
import { clamp } from '../../../shared/scalar';
import { isBeatTick } from '../../../shared/tempo';
import { hurtEnemy, keepWhere, touches } from '../../effects';
import type { SkillModule } from '../../skills/types';
import type { EnemyState, PlayerState, PropState, Vec2 } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { ownerOf } from '../../props';

export const PORTAL = 'meteorPortal';
export const METEOR = 'meteor';

export const meteors: SkillModule<MeteorsEffect> = {
  cast(ctx, player, effect, _input, power) {
    const { state } = ctx;
    const { width, height } = state.arena;
    const distance = aimedAt(state.enemies, player, effect) ?? effect.distance;
    const x = clamp(player.x + player.aim.x * distance, 0, width);
    const y = clamp(player.y + player.aim.y * distance, 0, height);
    const portal: PropState = {
      id: state.nextEntityId,
      kind: PORTAL,
      playerId: player.id,
      phase: 'flying',
      x,
      y,
      prevX: x,
      prevY: y,
      vx: 0,
      vy: 0,
      damage: effect.damage * power,
      ticksLeft: 0,
      hitsLeft: portalMeteors(player, effect),
      rank: 0,
    };
    state.nextEntityId += 1;
    (state.props ??= []).push(portal);
  },

  update(ctx) {
    const { state, tempo } = ctx;
    const props = state.props;
    if (props === undefined) {
      return;
    }
    const beat = isBeatTick(state.tick, tempo);
    let done = false;
    const launched: PropState[] = [];
    for (const prop of props) {
      if (prop.kind === PORTAL && beat) {
        launched.push(launch(ctx, prop));
        done ||= prop.hitsLeft === 0;
      } else if (prop.kind === METEOR) {
        prop.x += prop.vx;
        prop.y += prop.vy;
        prop.ticksLeft -= 1;
        if (prop.ticksLeft <= 0) {
          land(ctx, prop);
          done = true;
        }
      }
    }
    props.push(...launched);
    if (done) {
      keepWhere(
        props,
        (prop) => (prop.kind !== PORTAL && prop.kind !== METEOR) || prop.hitsLeft > 0,
      );
      if (props.length === 0) {
        delete state.props;
      }
    }
  },
};

function portalMeteors(player: PlayerState, effect: MeteorsEffect): number {
  return Math.floor(statValue(player, 'skillCountAdd', effect.meteors));
}

function meteorsOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): MeteorsEffect {
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'meteors') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" opens no portal`);
  }
  return effect;
}

function aimedAt(
  enemies: readonly EnemyState[],
  player: PlayerState,
  effect: MeteorsEffect,
): number | undefined {
  let nearest: number | undefined;
  for (const enemy of enemies) {
    const dx = enemy.x - player.x;
    const dy = enemy.y - player.y;
    const along = dx * player.aim.x + dy * player.aim.y;
    const across = Math.abs(dy * player.aim.x - dx * player.aim.y);
    if (
      enemy.hp > 0 &&
      along > 0 &&
      along <= effect.range &&
      across <= enemy.radius + effect.radius &&
      (nearest === undefined || along < nearest)
    ) {
      nearest = along;
    }
  }
  return nearest;
}

function launch(ctx: StepContext, portal: PropState): PropState {
  const { state } = ctx;
  const effect = meteorsOf(ctx, ownerOf(state, portal));
  const target = nearestTo(state.enemies, portal, effect.reach) ?? portal;
  const meteor: PropState = {
    id: state.nextEntityId,
    kind: METEOR,
    playerId: portal.playerId,
    phase: 'flying',
    x: portal.x,
    y: portal.y,
    prevX: portal.x,
    prevY: portal.y,
    vx: (target.x - portal.x) / effect.fallTicks,
    vy: (target.y - portal.y) / effect.fallTicks,
    damage: portal.damage,
    ticksLeft: effect.fallTicks,
    hitsLeft: 1,
    rank: portal.rank ?? 0,
  };
  state.nextEntityId += 1;
  portal.hitsLeft -= 1;
  portal.rank = (portal.rank ?? 0) + 1;
  return meteor;
}

function nearestTo(
  enemies: readonly EnemyState[],
  at: Vec2,
  reach: number,
): EnemyState | undefined {
  let nearest: EnemyState | undefined;
  let best = reach * reach;
  for (const enemy of enemies) {
    const dx = enemy.x - at.x;
    const dy = enemy.y - at.y;
    const distance = dx * dx + dy * dy;
    if (enemy.hp > 0 && distance <= best) {
      nearest = enemy;
      best = distance;
    }
  }
  return nearest;
}

function land(ctx: StepContext, meteor: PropState): void {
  const { state } = ctx;
  const owner = ownerOf(state, meteor);
  const effect = meteorsOf(ctx, owner);
  for (const enemy of state.enemies) {
    if (enemy.hp > 0 && touches(enemy, meteor, effect.radius)) {
      hurtEnemy(state, enemy, meteor.damage, owner.id, meteor);
    }
  }
  meteor.hitsLeft = 0;
}
